import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../context/LangContext';
import RichTextEditor from '../components/RichTextEditor';

export default function MailPage() {
  const { t, lang } = useLang();
  const [tab, setTab] = useState('templates');

  return (
    <>
      <div className="page-header">
        <h2>{t('nav_mail')}</h2>
      </div>
      <div className="page-body">
        <ul className="nav nav-tabs mb-3">
          {[['templates', t('mail_tab_templates')], ['settings', t('mail_tab_settings')], ['log', t('mail_tab_log')]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button className={`nav-link${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>{label}</button>
            </li>
          ))}
        </ul>
        {tab === 'templates' && <TemplatesTab t={t} />}
        {tab === 'settings'  && <SettingsTab t={t} />}
        {tab === 'log'       && <LogTab t={t} lang={lang} />}
      </div>
    </>
  );
}

// ── Templates ─────────────────────────────────────────────────
function TemplatesTab({ t }) {
  const [templates, setTemplates] = useState([]);
  const [fields, setFields]       = useState([]);
  const [edit, setEdit]           = useState(null); // null | {id?, name, subject, body_html, is_default}
  const [error, setError]         = useState('');

  function load() {
    api.get('/api/mail/templates').then(r => setTemplates(r.data));
  }
  useEffect(() => {
    load();
    api.get('/api/mail/merge-fields').then(r => setFields(r.data)).catch(() => {});
  }, []);

  async function save() {
    setError('');
    if (!edit.name || !edit.subject || !edit.body_html) { setError(t('mail_tpl_required')); return; }
    try {
      if (edit.id) await api.put(`/api/mail/templates/${edit.id}`, edit);
      else await api.post('/api/mail/templates', edit);
      setEdit(null); load();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    }
  }
  async function remove(id) {
    if (!window.confirm(t('mail_tpl_delete_confirm'))) return;
    await api.delete(`/api/mail/templates/${id}`);
    load();
  }

  if (edit) {
    return (
      <div className="card"><div className="card-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <div className="row g-3">
          <div className="col-md-8">
            <div className="mb-2">
              <label className="form-label small fw-medium">{t('mail_tpl_name')} *</label>
              <input className="form-control form-control-sm" value={edit.name}
                onChange={e => setEdit({ ...edit, name: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label small fw-medium">{t('mail_subject')} *</label>
              <input className="form-control form-control-sm" value={edit.subject}
                onChange={e => setEdit({ ...edit, subject: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label small fw-medium">{t('mail_body')} *</label>
              <RichTextEditor value={edit.body_html}
                onChange={html => setEdit({ ...edit, body_html: html })} minHeight={220} />
            </div>
            <div className="form-check mb-3">
              <input className="form-check-input" type="checkbox" id="tplDefault"
                checked={!!edit.is_default} onChange={e => setEdit({ ...edit, is_default: e.target.checked })} />
              <label className="form-check-label small" htmlFor="tplDefault">{t('mail_tpl_default')}</label>
            </div>
            <button className="btn btn-primary btn-sm me-2" onClick={save}>{t('save')}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEdit(null)}>{t('cancel')}</button>
          </div>
          <div className="col-md-4">
            <div className="small fw-medium mb-2">{t('mail_merge_fields')}</div>
            <div className="d-flex flex-column gap-1" style={{ fontSize: 12 }}>
              {fields.map(f => (
                <div key={f.key}><code className="text-primary">{`{{${f.key}}}`}</code> <span className="text-muted">— {f.label}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div></div>
    );
  }

  return (
    <div className="card"><div className="card-body">
      <button className="btn btn-primary btn-sm mb-3"
        onClick={() => setEdit({ name: '', subject: '', body_html: '', is_default: false })}>
        <i className="bi bi-plus me-1" />{t('mail_tpl_new')}
      </button>
      {templates.length === 0 ? (
        <div className="text-muted">{t('mail_tpl_none')}</div>
      ) : (
        <table className="table table-hover align-middle mb-0">
          <thead><tr><th>{t('mail_tpl_name')}</th><th>{t('mail_subject')}</th><th /><th style={{ width: 90 }} /></tr></thead>
          <tbody>
            {templates.map(tpl => (
              <tr key={tpl.id}>
                <td>{tpl.name}{tpl.is_default ? <span className="badge bg-secondary ms-2">{t('mail_tpl_default')}</span> : null}</td>
                <td className="text-muted small">{tpl.subject}</td>
                <td />
                <td className="text-end">
                  <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => setEdit(tpl)} title={t('edit')}>
                    <i className="bi bi-pencil" />
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => remove(tpl.id)} title={t('delete')}>
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div></div>
  );
}

// ── Instellingen ──────────────────────────────────────────────
function SettingsTab({ t }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get('/api/mail/settings').then(r => setS(r.data || {})); }, []);
  async function save() {
    await api.put('/api/mail/settings', s);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }
  if (!s) return <div className="spinner-border spinner-border-sm" />;
  return (
    <div className="card" style={{ maxWidth: 640 }}><div className="card-body">
      <div className="text-muted small mb-3">{t('mail_settings_hint')}</div>
      {[['default_reply_to', 'Reply-To'], ['default_cc', 'Cc'], ['default_bcc', 'Bcc']].map(([key, label]) => (
        <div className="mb-2" key={key}>
          <label className="form-label small fw-medium">{label}</label>
          <input className="form-control form-control-sm" value={s[key] || ''} onChange={e => setS({ ...s, [key]: e.target.value })} />
        </div>
      ))}
      <div className="mb-3">
        <label className="form-label small fw-medium">{t('mail_signature')}</label>
        <RichTextEditor value={s.signature_html || ''}
          onChange={html => setS({ ...s, signature_html: html })} minHeight={140} />
      </div>
      <button className="btn btn-primary btn-sm" onClick={save}>{t('save')}</button>
      {saved && <span className="text-success small ms-2"><i className="bi bi-check-circle me-1" />{t('saved')}</span>}
    </div></div>
  );
}

// ── Log ───────────────────────────────────────────────────────
function LogTab({ t, lang }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/api/mail/log').then(r => setRows(r.data)).finally(() => setLoading(false)); }, []);
  const fmt = (s) => new Date(s).toLocaleString(lang === 'en' ? 'en-GB' : 'nl-BE');
  if (loading) return <div className="spinner-border spinner-border-sm" />;
  return (
    <div className="card"><div className="card-body">
      {rows.length === 0 ? <div className="text-muted">{t('mail_log_none')}</div> : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead><tr>
              <th>{t('mail_log_when')}</th><th>{t('mail_to')}</th><th>{t('mail_subject')}</th>
              <th>{t('rep_col_audit')}</th><th>{t('mail_log_status')}</th><th>{t('rep_col_by')}</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="text-nowrap small">{fmt(r.sent_at)}</td>
                  <td className="small">{r.to_addr}</td>
                  <td className="small">{r.subject}</td>
                  <td className="small text-muted">{r.audit_name || '—'}</td>
                  <td>
                    {r.status === 'sent'
                      ? <span className="badge bg-success">{t('mail_log_sent')}</span>
                      : <span className="badge bg-danger" title={r.error}>{t('mail_log_failed')}</span>}
                  </td>
                  <td className="small text-muted">{r.sent_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div></div>
  );
}
